import {IWixWindowViewMode} from '@wix/native-components-infra/dist/es/src/types/types'
import {IWixStyleParams, getStylesValues} from '@wix/tpa-settings'
import {viewerDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useEnvironment, useErrorBoundary, usePanorama} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useEffect, useMemo} from 'react'
import {DynamicStyle} from '../../../../../commons/components/dynamic-style'
import {ErrorPage} from '../../../../../commons/components/error-page'
import {Direction} from '../../../../../commons/constants/html'
import {DatesProvider} from '../../../../../commons/hooks/dates'
import {StylesForAllBreakpointsProvider} from '../../../../../commons/hooks/styles-for-all-breakpoints'
import stylesParams, {getNumbersAndBooleans} from '../../../stylesParams'
import {SettingsUpdatedActionType} from '../../actions/sdk'
import {WidgetActionsContext, WidgetStateContext} from '../../hooks/state-provider'
import {getEvents} from '../../selectors/events'
import {isButtonLayout} from '../../selectors/single-settings'
import {AppLoaded} from '../app-loaded'
import {Layout} from '../layout'
import {WidgetRuntimeContext} from '../runtime-context/connect'
import s from './app.scss'
import {AppProps} from './interfaces'

const ROOT_ELEMENT_ID = 'wix-events-widget'

export const App: React.FC<AppProps> = props => {
  const {state, isRTL, sdk, actions, host, pageInfo, featuredWidget} = props
  const {isMobile, isPreview} = useEnvironment()
  const {error} = useErrorBoundary()
  const {errorMonitor} = usePanorama()

  const defaultNumbersAndBooleans = useMemo(() => {
    const defaultStylesValues = getStylesValues(
      {
        numbers: {},
        booleans: {},
      } as unknown as IWixStyleParams,
      stylesParams,
      {
        isMobile,
        isRTL,
      },
    )

    return getNumbersAndBooleans(defaultStylesValues)
  }, [isMobile, isRTL])

  const updateStyle = styleParams => {
    actions.updateStyles(styleParams)
  }

  const updateSettings = action => {
    switch (action.type) {
      case SettingsUpdatedActionType.NAVIGATE_TO_PAGE:
      case SettingsUpdatedActionType.NAVIGATE_TO_PAGE_PENDING:
        if (action.type === SettingsUpdatedActionType.NAVIGATE_TO_PAGE_PENDING) {
          if (sdk?.Wix) {
            sdk.Wix.Data.Public.set(action.type, action.meta.arg, {scope: 'APP'}, undefined, undefined)
          }
        } else {
          if (sdk?.Wix) {
            sdk.Wix.Data.Public.set(action.type, action.page, {scope: 'APP'}, undefined, undefined)
          }
        }
        break
      default:
        actions.updateSettings(action)
    }
  }

  const onEditModeChange = () => {
    actions.setBaseEnvironment()
    actions.closeListLayoutItems()
  }

  useEffect(() => {
    if (sdk?.Wix) {
      sdk.Wix.addEventListener(sdk.Wix.Events.SETTINGS_UPDATED, updateSettings)
      sdk.Wix.addEventListener(sdk.Wix.Events.STYLE_PARAMS_CHANGE, updateStyle)
      sdk.Wix.addEventListener(sdk.Wix.Events.EDIT_MODE_CHANGE, onEditModeChange)
    }

    // Cheap way to inform Sled about end of hydration
    document.getElementById(ROOT_ELEMENT_ID)?.setAttribute('data-hydrated', 'true')

    return () => {
      if (sdk?.Wix) {
        // @ts-expect-error
        sdk.Wix.removeEventListener(sdk.Wix.Events.SETTINGS_UPDATED, updateSettings)
        // @ts-expect-error
        sdk.Wix.removeEventListener(sdk.Wix.Events.STYLE_PARAMS_CHANGE, updateStyle)
        // @ts-expect-error
        sdk.Wix.removeEventListener(sdk.Wix.Events.EDIT_MODE_CHANGE, onEditModeChange)
      }
    }
  }, [])

  useEffect(() => {
    if (actions && host && state) {
      if (isPreview && host.formFactor) {
        actions.setFormFactor(host.formFactor)
      }
    }
  }, [host?.formFactor])

  useEffect(() => {
    if (!state || error) {
      errorMonitor().reportError(new Error('Failed to load events widget', {cause: error || 'State is not loaded'}))
    }
  }, [error, state])

  if (!state || error) {
    return <ErrorPage viewMode={host.viewMode} />
  }

  const {
    host: {viewMode, style, id},
    actions: {widgetLoaded},
  } = props

  const settings = {
    ...defaultNumbersAndBooleans,
    ...state.component.settings,
    ...style.styleParams.numbers,
    ...style.styleParams.booleans,
  }

  return (
    <WidgetRuntimeContext.Provider value={props}>
      <WidgetStateContext.Provider value={{...state, component: {...state.component, settings}}}>
        <WidgetActionsContext.Provider value={actions}>
          <DatesProvider dates={state.dates}>
            <StylesForAllBreakpointsProvider>
              <DynamicStyle namespace={id}>
                {`
                  .${s.root} {
                    --eventsCount: ${getEvents(state).length};
                  }
                `}
              </DynamicStyle>
              <div
                id={ROOT_ELEMENT_ID}
                className={classNames(s.root, {
                  [s.visibleOverflow]: isButtonLayout(state.component) || featuredWidget,
                })}
                dir={isRTL ? Direction.RTL : Direction.LTR}
                data-hook={DH.ROOT_NODE}
              >
                <AppLoaded
                  host={host}
                  widgetLoaded={widgetLoaded}
                  viewMode={viewMode as IWixWindowViewMode}
                  isHomePage={pageInfo.isHomePage}
                />
                <Layout />
              </div>
            </StylesForAllBreakpointsProvider>
          </DatesProvider>
        </WidgetActionsContext.Provider>
      </WidgetStateContext.Provider>
    </WidgetRuntimeContext.Provider>
  )
}
